import { Component, HostListener, OnInit, Input, OnDestroy, Output, EventEmitter, signal } from '@angular/core';
import { contentLanguageUpdated, NotificationService } from '../../../shared/services/notification.service';
import { UserService } from '../../../shared/services/user.service';
import { ProjectService } from '../../../shared/services/project.service';
import { OverlayService } from '../../../shared/services/overlayService';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MtmDatePipe } from '../../../shared/pipes/mtm-date.pipe';
import { Subject, forkJoin } from 'rxjs';
import { ClickableNotificationService } from '../../../shared/services/clickable-notification.service';
import { WorkspaceService } from '../../../shared/services/workspace.service';
import { AuthService } from '../../../shared/services/auth.service';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { NotificationViewMode } from '../../../shared/interfaces';
import { CryptoService } from '../../../shared/services/crypto.service';
import { ProjectSectionService } from '../../../shared/services/project-section.service';
import { PlatformCommentsChatStateService } from '../../../shared/services/platform-comments-chat-state.service';
import { AppStateService } from '../../../shared/services/app-state.service';
import { SubsectionService } from '../../../shared/services/subsection.service';
import { ElementPathNode, normalizeElementPath } from '../../../shared/utils/element-path.utils';
import { Dialog } from '@angular/cdk/dialog';
import { openContentUnavailableDialog } from '../../../errors/content-unavailable/content-unavailable-dialog.component';
import { ORG_ELEMENT_TYPES } from '../../../shared/services/notification-helpers/drive-comment.utils';

@Component({
  selector: 'notification-view',
  templateUrl: './notification-view.component.html',
  styleUrl: './notification-view.component.scss',
  standalone: false,
})
export class NotificationViewComponent implements OnInit, OnDestroy {
  @Input() notification: any = null;
  @Input() isNotificationProject: boolean = true;
  @Input() mode: NotificationViewMode = NotificationViewMode.List;
  @Input() showSectionBorder: boolean = false;
  @Input() duration: number;
  @Input() showCloseButton: boolean = false;
  @Output() notificationFinish: EventEmitter<any> = new EventEmitter();

  get avatarSize(): number {
    if (this.screenWidth < 600) return 50;
    if (this.screenWidth < 700) return 58;
    if (this.screenWidth < 900) return 65;
    return 70;
  }

  get isExtTaskNotification(): boolean {
    return typeof this.notification?.code === 'string' && this.notification.code.startsWith('EXT_TASK_');
  }

  get pathSegments(): string[] {
    const obj = this.notification?.object;
    if (!obj) {
      return [];
    }

    const path: Record<string, ElementPathNode> = normalizeElementPath(obj.elementPath);

    const segments: string[] = [];
    const workspaceName = path['WORKSPACE']?.name ?? obj.workspaceName;
    const campaignName = path['CAMPAIGN']?.name;
    const elementName = obj.elementProperties?.name ?? obj.elementName;

    if (workspaceName) segments.push(workspaceName);
    if (campaignName) segments.push(campaignName);
    if (elementName) segments.push(elementName);

    return segments;
  }

  private readonly notificationDisplaySignal = signal<any>(null);
  private readonly nameVisibleSignal = signal<boolean>(false);
  private readonly workspaceNameSignal = signal<string>('');
  private readonly screenWidthSignal = signal<number>(0);
  private readonly showAllWorkspacesSignal = signal<boolean>(false);
  private readonly projectMovedSignal = signal<boolean>(false);
  private readonly viewOpacitySignal = signal<number>(1);

  get notificationDisplay(): any {
    return this.notificationDisplaySignal();
  }
  set notificationDisplay(value: any) {
    this.notificationDisplaySignal.set(value ?? null);
  }

  get nameVisible(): boolean {
    return this.nameVisibleSignal();
  }
  set nameVisible(value: boolean) {
    this.nameVisibleSignal.set(!!value);
  }

  get workspaceName(): string {
    return this.workspaceNameSignal();
  }
  set workspaceName(value: string) {
    this.workspaceNameSignal.set(value ?? '');
  }

  get screenWidth(): number {
    return this.screenWidthSignal();
  }
  set screenWidth(value: number) {
    this.screenWidthSignal.set(value ?? 0);
  }

  get showAllWorkspaces(): boolean {
    return this.showAllWorkspacesSignal();
  }
  set showAllWorkspaces(value: boolean) {
    this.showAllWorkspacesSignal.set(!!value);
  }

  get projectMoved(): boolean {
    return this.projectMovedSignal();
  }
  set projectMoved(value: boolean) {
    this.projectMovedSignal.set(!!value);
  }

  get viewOpacity(): number {
    return this.viewOpacitySignal();
  }
  set viewOpacity(value: number) {
    this.viewOpacitySignal.set(value ?? 1);
  }

  // Signal-backed notificationDisplay
  hasName = true;
  hasObject = true;
  ngUnsubscribe = new Subject();
  myWorkspaces: any[] = [];
  otherWorkspaces: any[] = [];
  filteredWorkspaces: any[] = [];
  authUser: any = null;
  workspaces: any[] = [];
  workspaceData: any;

  interval: any;

  NotificationViewType = NotificationViewMode;

  // check user
  isHrUser: boolean = false;
  isYslUser: boolean = false;
  userType: string;

  constructor(
    private readonly notificationService: NotificationService,
    public userService: UserService,
    private readonly projectService: ProjectService,
    private readonly projectSectionService: ProjectSectionService,
    private readonly mtmDate: MtmDatePipe,
    private readonly overlayService: OverlayService,
    private readonly clickableNotificationService: ClickableNotificationService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly dialog: Dialog,
    private readonly workspaceService: WorkspaceService,
    private readonly authService: AuthService,
    private readonly translatePipe: TranslatePipe,
    private readonly cryptoService: CryptoService,
    private readonly platformChatState: PlatformCommentsChatStateService,
    private readonly appState: AppStateService,
    private readonly subsectionService: SubsectionService
  ) {
    this.screenWidth = window.innerWidth;
    contentLanguageUpdated.subscribe((lang) => {
      setTimeout(() => {
        this.notification.notificationDisplay = this.notificationService.getMessage(
          this.notification.code,
          this.notification.object
        );
        this.notificationDisplay = this.notification.notificationDisplay;
      }, 500);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.screenWidth = window.innerWidth;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  ngOnInit() {
    this.authUser = this.authService.getAuthUser();
    this.workspaces = this.workspaceService.getCachedWorkspaces();
    this.filteredWorkspaces = this.workspaces;
    if (!Object.prototype.hasOwnProperty.call(this.notification, 'object') || this.notification.object == null) {
      this.hasObject = false;
      this.hasName = false;
    } else {
      if (!Object.prototype.hasOwnProperty.call(this.notification.object, 'sectionName')) {
        this.hasName = false;
      }
    }

    if (this.notification?.object) {
      if (this.notification.object.notifiedByUsername && !this.notification.object.notifiedByAvatarUrl) {
        this.notification.object.notifiedByAvatarUrl = this.userService.getAvatarUrl(
          this.notification.object.notifiedByUsername
        );
        this.notificationDisplay = this.notification.notificationDisplay;
      } else this.notificationDisplay = this.notification.notificationDisplay;
    }
    //this.notificationService.changeMessage(this.notificationDisplay.label);
    //hiding project name when project is moved from workspace to another workspace
    if (this.notification.projectId === '00000000-0000-0000-0000-000000000000') {
      this.projectMoved = true;
    } else {
      this.projectMoved = false;
    }
    if (this.mode == this.NotificationViewType.Toast) {
      this.startToastNotification();
    }
  }

  mouseEnter() {
    this.nameVisible = true;
  }

  mouseLeave() {
    this.nameVisible = false;
  }

  markNotificationsAsSeen(projectId: string | undefined) {
    this.notificationService.markNotificationsAsSeen(projectId).subscribe(
      (response) => {
        console.log('Notifications marked as seen:', response);
      },
      (error) => {
        console.error('Error marking notifications as seen:', error);
      }
    );
  }

  formatDate(dateTime) {
    return this.mtmDate.transform(dateTime);
  }

  onNotificationClick(e: any, notification: any) {
    e.preventDefault();
    notification.seen = true;
    if (notification.object?.channelUuid && notification.object?.messageUuid) {
      const activePage = this.appState.getActivePage();
      if (activePage) {
        this.appState.setCommentPageCommentExpanded(activePage, true);
      }
      this.subsectionService.updateUIState({ commentsCollapsed: false, rightPanelView: 'comment' });

      const targetId = this.clickableNotificationService.getNotificationTargetId(notification.object);
      this.platformChatState.requestNavigation({
        channelId: notification.object.channelUuid,
        messageId: notification.object.messageUuid,
        parentMessageId: notification.object.parentMessageUuid,
        targetId,
      });
    }

    if (typeof notification.code === 'string' && notification.code.startsWith('EXT_TASK_')) {
      void this.clickableNotificationService.goToTaskPage(notification);
      this.markNotificationsAsSeen(notification);
      return;
    }

    if (notification.projectId === '00000000-0000-0000-0000-000000000000') {
      // No-project notifications (e.g. SHARE_INVITE_SENT) may carry a BE-provided route
      const path = notification.object?.path;
      if (typeof path === 'string' && path) {
        void this.router.navigateByUrl(path);
        this.markNotificationsAsSeen(notification);
        return;
      }

      const elementType = notification.object?.elementType;

      if (ORG_ELEMENT_TYPES.has(elementType) || !elementType) {
        this.clickableNotificationService.navigateToOrgEntity(notification.object ?? {});
        this.markNotificationsAsSeen(notification);
        return;
      }

      if (elementType === 'PROJECT' && notification.object?.elementUuid) {
        notification.projectId = notification.object.elementUuid;
        if (notification.object) {
          notification.object.projectId = notification.object.elementUuid;
        }
      } else {
        const enriched = this.enrichFromElementPath(notification);
        if (!enriched) {
          this.workspaceName = notification.object?.workspaceName;
          this.popularWorkspaceGroups();
          this.workSpaceNavigation(notification);
          this.markNotificationsAsSeen(notification);
          return;
        }
      }
    }

    const projectId = notification?.projectId || notification?.object?.projectId;
    if (!projectId) {
      return;
    }

    if (this.projectService.project?.id && this.projectService.project.id === projectId) {
      this.clickableNotificationService.gotoNotificationSource(this.projectService.sections, notification);
    } else {
      // The project fetch only confirms it still exists / is accessible before leaving the current screen.
      forkJoin([
        this.projectSectionService.getSectionsAndSubsections(projectId),
        this.projectService.getSingleProject(projectId),
      ]).subscribe({
        next: ([res]: [any, unknown]) => {
          this.projectService.leaveFromProject();
          this.clickableNotificationService.gotoNotificationSource(res, notification);
        },
        error: () => openContentUnavailableDialog(this.dialog),
      });
    }

    this.markNotificationsAsSeen(notification);
  }

  private enrichFromElementPath(notification: any): boolean {
    const obj = notification.object;
    if (!obj?.elementPath) {
      return false;
    }
    const path: Record<string, ElementPathNode> = normalizeElementPath(obj.elementPath);
    const projectId = path['PROJECT']?.id;
    if (!projectId) {
      return false;
    }
    notification.projectId = projectId;
    obj.projectId = projectId;
    obj.sectionId = obj.sectionId || path['SECTION']?.id;
    obj.sectionName = obj.sectionName || path['SECTION']?.name;
    obj.subSectionId = obj.subSectionId || path['SUB-SECTION']?.id;
    obj.subSectionName = obj.subSectionName || path['SUB-SECTION']?.name;
    obj.topicId = obj.topicId || path['FOLDER']?.id;
    obj.optionId = obj.optionId || path['SUB-FOLDER']?.id;
    return true;
  }

  /**
   * @param notification setting routes for workspace
   */
  workSpaceNavigation(notification: any) {
    //when user create a new workspace
    if (notification.code === 'WORKSPACE_CREATED') {
      if (this.workspaceData !== undefined) {
        this.setActiveWorkspace(this.workspaceData);
      } else {
        this.router.navigate(['/projects']);
      }
    }
    // when user added to workspace redirect to workspace
    if (notification.code === 'WORKSPACE_ADDED_USER') {
      if (this.workspaceData !== undefined) {
        this.setActiveWorkspace(this.workspaceData);
      } else {
        this.openWorkspaceList();
      }
    }
    //when user deleted from a workspace
    if (notification.code === 'WORKSPACE_DELETED_USER') {
      this.router.navigate(['/projects']);
    }
    //when user role change
    if (notification.code === 'WORKSPACE_EDITED_USER') {
      this.router.navigate(['/projects']);
    }
    //when workspace owner change name of workspace
    if (notification.code === 'WORKSPACE_EDITED') {
      if (this.workspaceData !== undefined) {
        this.setActiveWorkspace(this.workspaceData);
      } else {
        this.openWorkspaceList();
      }
    }
    // workspace deleted
    if (notification.code === 'WORKSPACE_DELETED') {
      this.notificationService.open({
        title: this.translatePipe.transform('successful'),
        notificationType: 'success',
        centerHeader: true,
        notificationInfo: this.translatePipe.transform('notification_workspace_delete'),
        confirmBtn: this.translatePipe.transform('ok'),
      });
    }
    // When project moved to new workspace
    if (notification.code === 'WORKSPACE_MOVED_PROJECT') {
      this.projectMoved = false;
      if (this.workspaceData !== undefined) {
        this.setActiveWorkspace(this.workspaceData);
      } else {
        this.openWorkspaceList();
      }
    }

    if (notification.code === 'MILESTONE_COMMENT_TAG_USER') {
      const wid_ = this.cryptoService.encryptString(notification?.object?.workspaceId);
      const dc_ = this.cryptoService.encryptString(notification?.object?.campaignId);
      this.router.navigate(['workspaces', 'campaigndrive', 'timeline-setting'], {
        queryParams: { wid: wid_, dc: dc_ },
      });
    }
  }

  /**
   * for redirecting when workspace name is changes or it not found
   */
  openWorkspaceList() {
    this.showAllWorkspaces = true;
    this.workspaceService.unsetActiveWorkspace();
    this.router.navigate(['/projects']);
  }

  /**
   *  redirecting user to the workspace
   * @param workspace
   */
  setActiveWorkspace(workspace: any) {
    this.router.navigate(['/projects']);
    this.workspaceService.setActiveWorkspace(workspace);
  }

  /**
   *
   * @returns worskpace data for setting active workspace
   */
  private popularWorkspaceGroups() {
    if (!this.filteredWorkspaces) {
      this.myWorkspaces = [];
      this.otherWorkspaces = [];
      return;
    }
    this.myWorkspaces = this.filteredWorkspaces.filter((w) => w.companyId == this.authUser.company.id);
    const myWorkSpacedata = this.myWorkspaces.filter((val) => val.name === this.workspaceName);
    this.workspaceData = myWorkSpacedata[0];
  }

  startToastNotification() {
    this.interval = setInterval(() => {
      this.closeNotification();
    }, this.duration);
  }

  closeNotification() {
    if (this.interval) {
      clearInterval(this.interval);
      this.viewOpacity = 0;
      this.notificationFinish.emit(this.notification);
    }
  }

  onWokspaceV2NotificationClick(event, notification) {
    console.log('notification', notification);
    const notifCheck = notification.notificationDisplay.label;
    if (
      (notifCheck == 'Task has been completed' && notification.username == 'testim+scenario2.hr@mtm.video') ||
      notification.username == 'testim+scenario2.ysl@mtm.video'
    ) {
      this.router.navigate(['workspaces/campaigndrive/5806860095/dashboard'], {
        queryParams: { from: 'notification' },
      });
    }

    if (notifCheck == 'You are invited on a new' || notifCheck == 'You are invited on') {
      if (
        notification.username == 'testim+scenario2.hr@mtm.video' ||
        notification.username == 'testim+scenario2.ysl@mtm.video'
      ) {
        this.router.navigate(['workspaces/analytics/all-milestones']);
      } else {
        this.router.navigate(['workspaces/campaigndrive/recap'], { queryParams: { from: 'notification' } });
      }
    }

    if (notifCheck == 'There is an issue with') {
      this.router.navigate(['/workspaces/campaigndrive/5806860095/dashboard'], {
        queryParams: { milestone: 'GO KEY VISUALS' },
      });
    }

    if (notifCheck == 'The Hero Shoot task you created have been completed') {
      this.router.navigate(['/workspaces/analytics/all-milestones']);
    }
  }

  getProjectName(notification) {
    if (notification?.object?.projectName) {
      const { projectName } = notification.object;
      const shortenedName = projectName.length > 20 ? projectName.slice(0, 20) + '...' : projectName;
      return shortenedName;
    } else {
      return;
      // return console.log("Notification object or projectName is undefined.");
    }
  }
}
